import Icon from 'core/modules/atoms/Icon';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatButton from '../ChatButton';
import ChatLink from '../ChatLink';
import './ChatContentReferences.scss';

const REFERENCE_TYPES = {
  WEB_REFERENCE: 'WEB_REFERENCE',
  CONTENT_REFERENCE: 'CONTENT_REFERENCE',
};

const CONTENT_TYPES = {
  MINT: 'MINT',
  MINT_PAGE: 'MINT_PAGE',
  MINT_PAGE_DUAL: 'MINT_PAGE_DUAL',
};

function ChatContentReferences({ references }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(['chatbot']);

  if (!references || references.length === 0) {
    return null;
  }

  const hasMultipleReferences = references.length > 1;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const buildDeepLink = (reference) => {
    // Handle web references
    if (reference.type === REFERENCE_TYPES.WEB_REFERENCE && reference.url) {
      return reference.url;
    }

    // Handle content references
    if (reference.contentType === CONTENT_TYPES.MINT) {
      return `/course/${reference.course.id}/unit/${reference.unit.id}/lesson/${reference.lesson.id}`;
    } else if (reference.contentType === CONTENT_TYPES.MINT_PAGE || reference.contentType === CONTENT_TYPES.MINT_PAGE_DUAL) {
      return `/course/${reference.course.id}/lesson/${reference.lesson.id}`;
    }
    return `/viewerfull/${reference.contentId}`;
  };

  const getReferenceTitle = (reference) => {
    if (reference.type === REFERENCE_TYPES.WEB_REFERENCE) {
      return reference.title || t('chatbot:Web');
    }
    return reference.lesson?.title || t('chatbot:Course');
  };

  const isWebReference = (reference) => {
    return reference.type === REFERENCE_TYPES.WEB_REFERENCE;
  };

  // Group references by type
  const webReferences = references.filter((ref) => isWebReference(ref));
  const courseReferences = references.filter((ref) => !isWebReference(ref));
  const hasBothTypes = webReferences.length > 0 && courseReferences.length > 0;

  const renderReferenceLink = (reference, index) => {
    const isWeb = isWebReference(reference);
    const title = getReferenceTitle(reference);
    const link = buildDeepLink(reference);

    return (
      <ChatLink key={reference.contentId || reference.url || index} href={link} external={true} className="chat-content-references__link">
        {isWeb ? (
          <span className="chat-content-references__link-text">{title}</span>
        ) : (
          <span className="chat-content-references__link-text" dangerouslySetInnerHTML={{ __html: title }} />
        )}
      </ChatLink>
    );
  };

  return (
    <div className={`chat-content-references ${!hasMultipleReferences || isExpanded ? 'chat-content-references--expanded' : ''}`}>
      <div className="chat-content-references__header">
        <span className="chat-content-references__title">{t('chatbot:Source')}</span>
        {hasMultipleReferences && (
          <ChatButton
            onClick={toggleExpanded}
            type="button"
            icon={isExpanded ? 'circle-less' : 'icon-plus'}
            iconPosition="right"
            variant="text"
            iconColor="black"
          >
            {isExpanded ? t('chatbot:Show less') : t('chatbot:Show')}
          </ChatButton>
        )}
      </div>

      <div className={`chat-content-references__list ${hasMultipleReferences && !isExpanded ? 'chat-content-references__list--hidden' : ''}`}>
        {/* Course References Section */}
        {courseReferences.length > 0 && (
          <div className="chat-content-references__section">
            <div className="chat-content-references__section-header">
              <Icon type="school" className="chat-content-references__section-icon" color="black" size="s" />
              <span className="chat-content-references__section-title">{t('chatbot:Course')}:</span>
            </div>
            <div className="chat-content-references__section-links">{courseReferences.map((reference, index) => renderReferenceLink(reference, index))}</div>
          </div>
        )}

        {/* Separator between sections */}
        {hasBothTypes && <div className="chat-content-references__separator" />}

        {/* Web References Section */}
        {webReferences.length > 0 && (
          <div className="chat-content-references__section">
            <div className="chat-content-references__section-header">
              <Icon type="weblink" className="chat-content-references__section-icon" color="black" size="s" />
              <span className="chat-content-references__section-title">{t('chatbot:Web')}:</span>
            </div>
            <div className="chat-content-references__section-links">{webReferences.map((reference, index) => renderReferenceLink(reference, index))}</div>
          </div>
        )}
      </div>
    </div>
  );
}

ChatContentReferences.propTypes = {
  references: PropTypes.arrayOf(
    PropTypes.shape({
      // Common fields
      type: PropTypes.oneOf([REFERENCE_TYPES.CONTENT_REFERENCE, REFERENCE_TYPES.WEB_REFERENCE]),
      title: PropTypes.string,

      // Content reference fields
      contentId: PropTypes.string,
      contentType: PropTypes.string,
      course: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
      }),
      unit: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
      }),
      lesson: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
      }),

      // Web reference fields
      url: PropTypes.string,
      snippet: PropTypes.string,
      domain: PropTypes.string,
      metadata: PropTypes.shape({
        publishDate: PropTypes.string,
        author: PropTypes.string,
        score: PropTypes.number,
      }),
    })
  ).isRequired,
};

ChatContentReferences.defaultProps = {
  references: [],
};

export default ChatContentReferences;
